// src/features/townBudget/components/programs/Program_OtherRevenues.jsx

import React from "react";
import { ProgramBudget } from "../../../../budget/model/program/ProgramBudget.jsx";

/**
 * Town Program: Other Revenues
 *
 * Firestore docId should match your stored doc:
 *   revenues_other_revenues
 */
export const PROGRAM_OTHER_REVENUES_CONFIG = {
  fullName: "Other Revenues",
  docId: "revenues_other_revenues",

  fieldDefs: [
    { key: "probateCourtReimbursements", label: "Probate Court Reimbursements", valueType: "currency", sources: [{ block: "Other Revenues", key: "Probate Court Reimbursements" }], group: "reimbursements", defaultVisible: false },
    { key: "interestOnInvestments", label: "Interest on Investments", valueType: "currency", sources: [{ block: "Other Revenues", key: "Interest on Investments" }], group: "investmentIncome", defaultVisible: true },
    { key: "leaseInterestIncome", label: "Lease Interest Income", valueType: "currency", sources: [{ block: "Other Revenues", key: "Lease Interest Income" }], group: "leaseProperty", defaultVisible: false },
    { key: "leaseRevenue", label: "Lease Revenue", valueType: "currency", sources: [{ block: "Other Revenues", key: "Lease Revenue" }], group: "leaseProperty", defaultVisible: false },
    { key: "landSalesRentals", label: "Land Sales & Rentals", valueType: "currency", sources: [{ block: "Other Revenues", key: "Land Sales & Rentals" }], group: "leaseProperty", defaultVisible: true },
    { key: "miscellaneous", label: "Miscellaneous", valueType: "currency", sources: [{ block: "Other Revenues", key: "Miscellaneous" }], group: "miscellaneous", defaultVisible: true },

    { key: "educStudentActivities", label: "Educ Student Activities", valueType: "currency", sources: [{ block: "Other Revenues", key: "Educ Student Activities" }], group: "education", defaultVisible: false },
    { key: "educVoAgTuition", label: "Educ Vo Ag Tuition", valueType: "currency", sources: [{ block: "Other Revenues", key: "Educ Vo Ag Tuition" }], group: "education", defaultVisible: false },
    { key: "educationTuitionOther", label: "Education Tuition/Other", valueType: "currency", sources: [{ block: "Other Revenues", key: "Education Tuition/Other" }], group: "education", defaultVisible: false },

    { key: "purchasingAuctionSales", label: "Purchasing - Auction Sales", valueType: "currency", sources: [{ block: "Other Revenues", key: "Purchasing - Auction Sales" }], group: "purchasing", defaultVisible: false },
    { key: "purchasingCardRebates", label: "Purchasing Card Rebates", valueType: "currency", sources: [{ block: "Other Revenues", key: "Purchasing Card Rebates" }], group: "purchasing", defaultVisible: false },

    { key: "phyServRefundsSales", label: "PhyServ Refunds & Sales", valueType: "currency", sources: [{ block: "Other Revenues", key: "PhyServ Refunds & Sales" }], group: "publicWorks", defaultVisible: false },

    { key: "refuseRecycling", label: "Refuse Recycling", valueType: "currency", sources: [{ block: "Other Revenues", key: "Refuse Recycling" }], group: "publicWorks", defaultVisible: false },
    { key: "bulkyWasteFill", label: "Bulky Waste Fill", valueType: "currency", sources: [{ block: "Other Revenues", key: "Bulky Waste Fill" }], group: "publicWorks", defaultVisible: false },

    { key: "claimsReimbursements", label: "Claims Reimbursements", valueType: "currency", sources: [{ block: "Other Revenues", key: "Claims Reimbursements" }], group: "reimbursements", defaultVisible: false },
    { key: "publicSafetyPolice", label: "Public Safety Police", valueType: "currency", sources: [{ block: "Other Revenues", key: "Public Safety Police" }], group: "publicSafety", defaultVisible: false },
    { key: "healthInsuranceReimburs", label: "Health Insurance/Reimburs", valueType: "currency", sources: [{ block: "Other Revenues", key: "Health Insurance/Reimburs" }], group: "reimbursements", defaultVisible: false },
    { key: "youthFamilyServices", label: "Youth & Family Services", valueType: "currency", sources: [{ block: "Other Revenues", key: "Youth & Family Services" }], group: "communityServices", defaultVisible: false },

    { key: "libraryTrusteeAccount", label: "Library Trustee Account", valueType: "currency", sources: [{ block: "Other Revenues", key: "Library Trustee Account" }], group: "library", defaultVisible: false },
    { key: "libraryMiscellaneous", label: "Library Miscellaneous", valueType: "currency", sources: [{ block: "Other Revenues", key: "Library Miscellaneous" }], group: "library", defaultVisible: false },

    { key: "clinicalFees", label: "Clinical Fees", valueType: "currency", sources: [{ block: "Other Revenues", key: "Clinical Fees" }], group: "healthSafety", defaultVisible: false },

    { key: "attorneyFeesReimbursed", label: "Attorney Fees Reimbursed", valueType: "currency", sources: [{ block: "Other Revenues", key: "Attorney Fees Reimbursed" }], group: "reimbursements", defaultVisible: false },
    { key: "utilitiesReimbursed", label: "Utilities Reimbursed", valueType: "currency", sources: [{ block: "Other Revenues", key: "Utilities Reimbursed" }], group: "reimbursements", defaultVisible: false },

    { key: "towerLease", label: "Tower Lease", valueType: "currency", sources: [{ block: "Other Revenues", key: "Tower Lease" }], group: "leaseProperty", defaultVisible: false },

    { key: "unrealizedGainLossInventory", label: "Unrealized Gain Loss Inventory", valueType: "currency", sources: [{ block: "Other Revenues", key: "Unrealized Gain Loss Inventory" }], group: "investmentIncome", defaultVisible: false },
    { key: "icmaAdministrativeAllowance", label: "ICMA Administrative Allowance", valueType: "currency", sources: [{ block: "Other Revenues", key: "ICMA Administrative Allowance" }], group: "miscellaneous", defaultVisible: false },
  ],

  tables: [
    {
      id: "adopted",
      title: "Other Revenues – Approved",
      sectionKey: "Approved",
      fields: [
        "probateCourtReimbursements",
        "interestOnInvestments",
        "leaseInterestIncome",
        "leaseRevenue",
        "landSalesRentals",
        "miscellaneous",
        "educStudentActivities",
        "educVoAgTuition",
        "educationTuitionOther",
        "purchasingAuctionSales",
        "purchasingCardRebates",
        "phyServRefundsSales",
        "refuseRecycling",
        "bulkyWasteFill",
        "claimsReimbursements",
        "publicSafetyPolice",
        "healthInsuranceReimburs",
        "youthFamilyServices",
        "libraryTrusteeAccount",
        "libraryMiscellaneous",
        "clinicalFees",
        "attorneyFeesReimbursed",
        "utilitiesReimbursed",
        "towerLease",
        "unrealizedGainLossInventory",
        "icmaAdministrativeAllowance",
      ],
      columnStyles: {
        year: { width: "100px", minWidth: "100px" },
        interestOnInvestments: { minWidth: "190px" },
        landSalesRentals: { minWidth: "170px" },
        miscellaneous: { minWidth: "150px" },
      },
    },
    {
      id: "actual",
      title: "Other Revenues – Actual",
      sectionKey: "Actual",
      fields: [
        "probateCourtReimbursements",
        "interestOnInvestments",
        "leaseInterestIncome",
        "leaseRevenue",
        "landSalesRentals",
        "miscellaneous",
        "educStudentActivities",
        "educVoAgTuition",
        "educationTuitionOther",
        "purchasingAuctionSales",
        "purchasingCardRebates",
        "phyServRefundsSales",
        "refuseRecycling",
        "bulkyWasteFill",
        "claimsReimbursements",
        "publicSafetyPolice",
        "healthInsuranceReimburs",
        "youthFamilyServices",
        "libraryTrusteeAccount",
        "libraryMiscellaneous",
        "clinicalFees",
        "attorneyFeesReimbursed",
        "utilitiesReimbursed",
        "towerLease",
        "unrealizedGainLossInventory",
        "icmaAdministrativeAllowance",
      ],
      columnStyles: {
        year: { width: "100px", minWidth: "100px" },
        interestOnInvestments: { minWidth: "190px" },
        landSalesRentals: { minWidth: "170px" },
        miscellaneous: { minWidth: "150px" },
      },
    },
  ],

  chartTitle: "Other Revenues Line Other Revenues (Actual preferred)",
};

export function ProgramOtherRevenues(props) {
  return (
    <ProgramBudget
      programId="revenues_other_revenues"
      config={PROGRAM_OTHER_REVENUES_CONFIG}
      {...props}
    />
  );
}
